import { computed, defineAsyncComponent, nextTick, onMounted, reactive, ref, toRefs, onBeforeUnmount } from 'vue';
import colors from '../colors/colors.vue.component';
import colorThumbnailsList from '../color-thumbnails/color-thumbnails-list.vue.component.js';
import colorSlider from '../colors/color-beauty-slider.vue.component';
import EventBus from '../../scripts/lib/EventBus.js';
import imageView from '../image-view/image-view.vue.component';
import badge from '../badge/badge-liste.vue.component';
import { trackCubePageViewPds, trackProductView, trackSizeSelected } from '../tracking/trackingLayer.js';
import wishlistContainer from '../wishlist-container/wishlist-container.vue.component';
import addToCart from '../add-to-cart/add-to-cart.vue.component';
import priceArea from '../price-area/price-area.vue.component';
import additionalPriceInfo from '../price-area/additional-price-info.vue.component';
import colorText from '../colors/color-text.vue.component';
import certificate from '../certificate/certificate.vue.component';
import certifiedColumns from '../certified-columns/certified-columns.vue.component';
import { trackABTests } from '../tracking/trackingABTest.js';

const i18n = JSON.parse(document.querySelector('#vue-data-i18n').textContent);

export default ({
  syan,
  articles,
  initialKey,
  productJSON,
  productDetailsJSON,
  eventTrackingJSON,
  metaLanguageJSON,
  appRoot,
  mandatedHost,
  wishlistApiTimeout,
  fitaRecoCall,
  showChristmasBear,
  isBreuningerReferrer,
}) => ({
  components: {
    'add-to-cart': addToCart,
    'additional-price-info': additionalPriceInfo,
    'certified-columns': certifiedColumns,
    'color-beauty-slider': colorSlider,
    'color-list': colors,
    'color-text': colorText,
    'color-thumbnail-list': colorThumbnailsList,
    'price-area': priceArea,
    'size-layer': defineAsyncComponent(() => import('../size-layer/size-layer.vue.component')),
    'image-main-image-view': imageView,
    'product-detail-accordion': defineAsyncComponent(
      () => import('../product-detail-accordion/product-detail-accordion.vue.component'),
    ),
    'badge-liste': badge,
    'partner-info-box': defineAsyncComponent(() => import('../partner-info-box/partner-info-box.vue.component')),
    certificate,
    wishlistContainer,
  },

  setup() {
    const getInitialLastSelectedSize = () => {
      if (!isBreuningerReferrer) {
        return null;
      }
      return articles[initialKey]?.sizes?.active?.name ?? null;
    };

    const state = reactive({
      productId: syan,
      active: articles[initialKey],
      activeImage: {},
      articleId: initialKey,
      initialArticleId: initialKey,
      salesId: articles[initialKey]?.salesId, // Use optional chaining for safety
      lastSelectedSize: getInitialLastSelectedSize(),
      articleMap: articles,
      i18n,
      limitedSaveValue: false,
      exclusiveSaveValue: false,
      isMixAndMatchSaveValue: false,
      isKitSaveValue: false,
      trackingStaticData: eventTrackingJSON,
      previewArticle: null,
      previewImage: null,
      previewIsDisplayed: false,
      metaLanguageData: metaLanguageJSON,
      productName: productJSON.name,
      brandName: productJSON.brandName,
      mandatedHost,
      showChristmasBear,
      isBreuningerReferrer,
    });

    const isNew = ref(null);
    const isEasyIron = ref(null);
    const wishlistItems = ref([]);

    const productDetails = computed(() => {
      if (productDetailsJSON === '') {
        return {};
      }
      const blueprint = productDetailsJSON.blueprint;
      const articleContent = productDetailsJSON[state.previewArticle?.colors?.active?.colorId || state.articleId];

      return {
        ...blueprint,
        ...articleContent,
      };
    });

    const displayPrice = computed(() => {
      if (state.previewArticle?.price) {
        return state.previewArticle.price;
      }
      return state.active.price;
    });

    const colorChange = (colorId) => {
      const articleId = getArticleIdForColor(colorId);

      selectArticleById(articleId, false);
      triggerVariantChangeEvents();
      updateCombiModuleColor();
      updateVspColor();
      changeMetaLanguageHeader();
    };

    const getArticleIdForColor = (colorId) => {
      const jsonArticle = productJSON.articles[colorId];

      let articleNumber = colorId;

      // keep the selected size, when change colors
      if (jsonArticleHasColors(jsonArticle) && jsonArticleHasSizes(jsonArticle)) {
        jsonArticle.sizes.values.forEach((item) => {
          if (item.name === state.lastSelectedSize && item.articleNumber !== undefined) {
            articleNumber = item.articleNumber;
          }
        });
      }

      return articleNumber;
    };

    function getArticleForColor(colorId) {
      return productJSON.articles[getArticleIdForColor(colorId)];
    }

    // When no color is pre-selected (single color dimension, e.g. beauty) colors.active.colorId is undefined.
    // Fall back to the representative article's color id instead of forwarding "undefined".
    function getActiveOrRepresentativeColorId() {
      return state.active.colors?.active?.colorId ?? articles[state.active.salesId]?.colors?.active?.colorId;
    }

    function updateCombiModuleColor() {
      const colorId = getActiveOrRepresentativeColorId();
      if (colorId === undefined || colorId === null) {
        return;
      }
      const combiModule = document.querySelector('.ents-combi-module-container reco-kombi-modul-loader');
      combiModule?.setAttribute('color-id', colorId);
    }

    function updateVspColor() {
      const colorId = getActiveOrRepresentativeColorId();
      if (colorId === undefined || colorId === null) {
        return;
      }
      document.querySelectorAll('reco-vsp-loader').forEach((vspLoader) => {
        if (document.querySelector('ents-vsp-modal')?.contains(vspLoader)) {
          return;
        }
        vspLoader.setAttribute('color-id', colorId);
      });
    }

    const triggerVariantChangeToGwpbanner = () => {
      const gwpbanner = document.querySelector('camp-gwpbanner');
      if (gwpbanner) {
        if (state.active.colors !== undefined && state.active.colors.active.colorId !== undefined) {
          gwpbanner.setAttribute('farb-id', state.active.colors.active.colorId);
        } else {
          gwpbanner.removeAttribute('farb-id');
        }

        if (state.salesId === undefined || state.salesId === null) {
          gwpbanner.removeAttribute('artikel-id');
        } else {
          gwpbanner.setAttribute('artikel-id', state.salesId);
        }
      }
    };

    const colorPreview = (articleId) => {
      if (articleId === undefined || articleId === null) {
        state.previewIsDisplayed = false;
        // delay slightly to not drop preview due to javascript event speed hovering between pictures
        window.setTimeout(() => {
          if (!state.previewIsDisplayed) {
            state.previewArticle = null;
            state.previewImage = null;
            fireFinalPriceEvent(!displayPrice.value.isFromPrice, articleId);
            firePriceChangedEvent();
          }
        }, 50);
      } else {
        state.previewIsDisplayed = true;
        state.previewImage = articles[articleId].views[0];
        state.previewArticle = articles[articleId];
        fireFinalPriceEvent(!displayPrice.value.isFromPrice, articleId);
        firePriceChangedEvent();
      }
    };

    function fireFinalPriceEvent(includeInitialSalesInformationId, articleId) {
      const idChain = getActiveArticleIdChainWithoutEntsDummyValues(includeInitialSalesInformationId, articleId);
      const finalPriceUpdatePayload = {
        syan: idChain.syan,
        farbId: idChain.colorId,
        artikelId: idChain.articleId,
        vertriebsinfoId: idChain.salesInformationId,
      };
      document.dispatchEvent(
        new CustomEvent('kaufen:finalpreis:update', {
          detail: finalPriceUpdatePayload,
        }),
      );
      appRoot.setAttribute('data-test-ents-last-emitted-final-price-payload', JSON.stringify(finalPriceUpdatePayload));
    }

    function firePriceChangedEvent() {
      const currentPriceInMinorUnits = displayPrice.value.schemaPriceInCents;
      document.dispatchEvent(
        new CustomEvent('ents:pdp:price:changed', {
          detail: { price: currentPriceInMinorUnits },
        }),
      );
      appRoot.setAttribute(
        'data-test-ents-last-emitted-price-changed-payload',
        JSON.stringify({ price: currentPriceInMinorUnits }),
      );
    }

    function dimensionChange(selectParams) {
      if (selectParams.dimension === 'sizes') {
        EventBus.emit('ents:vue:sizes:highlight:show', {
          ctaAfterSizeSelection: selectParams.ctaAfterSizeSelection,
          overrideEventContext: selectParams.overrideEventContext,
        });
      } else {
        const $layer = document.querySelector('#ents-layer__colors');
        if ($layer != null) {
          $layer.classList.remove('hidden');
          $layer.show();
        }
      }
    }

    function layerHide() {
      const $layer = document.querySelector('#ents-layer__colors');
      if ($layer != null) {
        $layer.hide();
      }
    }

    function setNewActiveArticle(articleId) {
      state.active = articles[articleId];
    }

    const articleChange = (articleId) => {
      selectArticleById(articleId || state.articleId, false);
      triggerVariantChangeEvents();
      state.lastSelectedSize = state.active.sizes.active.name;
      changeMetaLanguageHeader();
    };

    function changeMetaLanguageHeader() {
      if (state.articleId in state.metaLanguageData) {
        for (const metaTag of state.metaLanguageData[state.articleId]) {
          document.querySelector(`meta[name="${metaTag.name}"]`)?.remove();
          const metaTagElement = document.createElement('meta');
          metaTagElement.setAttribute('name', metaTag.name);
          metaTagElement.setAttribute('content', metaTag.content);
          document.head.append(metaTagElement);
        }
      }
    }

    function jsonArticleHasColors(jsonArticle) {
      return jsonArticle !== undefined && jsonArticle.colors;
    }

    function jsonArticleHasSizes(jsonArticle) {
      return jsonArticle !== undefined && jsonArticle.sizes && jsonArticle.sizes.values.length > 1;
    }

    function selectArticleById(articleId, init) {
      const oldImages = state.active.views;
      const newImages = articles[articleId].views;

      if (compareImageArrays(oldImages, newImages)) {
        // only update images if they differ between current and new article
        const images = state.active.views;
        setNewActiveArticle(articleId);
        state.active.views = images;
      } else {
        setNewActiveArticle(articleId);
      }

      if (hasAliasArticle.value) {
        selectAliasArticle();
      } else {
        state.articleId = articleId;
        state.salesId = state.active.salesId;
      }

      if (isInitialArticle.value) {
        selectInitialArticle();
      }

      rewriteURI(init);
      updateProductFamilyUri();
    }

    function compareImageArrays(oldImages, newImages) {
      return oldImages.every((v, i) => v.standardUrl === newImages[i].standardUrl);
    }

    const hasAliasArticle = computed(() => state.active.alias !== undefined || false);

    function selectAliasArticle() {
      state.articleId = state.active.alias;
      state.salesId = state.active.salesId;
    }

    const isInitialArticle = computed(() => state.active.initial || false);

    function selectInitialArticle() {
      // use backend property names directly — mandatoryDimension returns event names ('sizes'/'colors'), not data keys
      const backendKey = state.active.sizes ? 'sizes' : 'colors';
      if (state.active[backendKey] && state.active[backendKey].values.length === 1) {
        selectArticleById(state.active[backendKey].values[0].articleNumber, true);
      }
    }

    const mandatoryDimension = computed(() => {
      // FIXME: introduce flag in json data?
      return state.active.sizes ? 'sizes' : 'colors';
    });

    function rewriteURI(init) {
      const location = window.location;
      const pathname = location.pathname;
      const queryString = location.search;

      if (window.history.replaceState) {
        let uri;
        if (init) {
          uri = pathname + queryString.replace(/(variant=)[^&]+/, `$1${state.articleId}`);
        } else {
          uri = `${pathname}?variant=${state.articleId}`;
        }
        window.history.replaceState(null, null, uri);
      }
    }

    function updateProductFamilyUri() {
      EventBus.emit('ents:vue:product-family:update', state.active.productFamilyUri);
    }

    function triggerVariantChangeEvents() {
      sendProductViewTracking();
      triggerExternalVariantChangeEvent();
      triggerVariantChangeToGwpbanner();
    }

    function sendProductViewTracking() {
      const idChain = getActiveArticleIdChainWithoutEntsDummyValues();
      const deliveryForecast = eventTrackingJSON.lieferprognose[idChain.articleId];

      const productViewDetails = {
        event_data: {
          is_interaction_event: false,
          viewed_item_flags: [],
          ressort: eventTrackingJSON.worldAssignment ?? '',
        },
        currency: {
          code: eventTrackingJSON.waehrung,
        },
        items: [
          {
            syan: idChain.syan,
            farb_id: idChain.colorId,
            artikel_id: idChain.articleId,
            vertriebsinfo_id: idChain.salesInformationId,
            delivery_status_min_days: deliveryForecast ? deliveryForecast.minimumShippingDays : null,
            delivery_status_max_days: deliveryForecast ? deliveryForecast.maximumShippingDays : null,
            is_available: !state.active.outOfStock,
          },
        ],
      };

      // There are a max of 3 flags visible at once on the PDS. Tracking should only contain visible tags.
      // That´s why we need to make sure, the flag conditions are in the same order as they are on the badge-liste.peb
      // to make sure the right ones are getting added to the tracking.
      const flagsConditions = [
        {
          condition: () => state.active.price.saleFlagContent,
          flag: 'sale',
        },
        {
          condition: () => state.active.exclusive,
          flag: 'exklusiv',
        },
        {
          condition: () => state.active.limited,
          flag: 'limitiert',
        },
        {
          condition: () => isNew.value && !state.active.price.saleFlagContent,
          flag: 'neu',
        },
        {
          condition: () => productDetails.value.certifiedColumns && productDetails.value.certifiedColumns.length > 0,
          flag: 'zertifiziert',
        },
        {
          condition: () => state.active.isMixAndMatch || state.active.isKit,
          flag: 'mix & match',
        },
        {
          condition: () => isEasyIron.value,
          flag: 'buegelleicht',
        },
      ];

      flagsConditions.some(({ condition, flag }) => {
        if (productViewDetails.event_data.viewed_item_flags.length >= 3) {
          return true; // Stop the iteration if 3 flags have been added
        }

        if (condition()) {
          productViewDetails.event_data.viewed_item_flags.push(flag);
        }

        return false;
      });

      trackProductView(productViewDetails);
    }

    function triggerExternalVariantChangeEvent() {
      const idChain = getActiveArticleIdChainWithoutEntsDummyValues();

      const payload = {
        syan: idChain.syan,
        colorId: idChain.colorId,
        articleId: idChain.articleId, // @deprecated use itemId (official DIST term)
        itemId: idChain.articleId,
        salesInformationId: idChain.salesInformationId, // @deprecated use skuGroupId (official DIST term)
        skuGroupId: idChain.salesInformationId,
      };

      appRoot.setAttribute('data-test-ents-last-emitted-variant-change-payload', JSON.stringify(payload));
      document.dispatchEvent(
        new CustomEvent('ents:pdp:variant:changed', {
          detail: payload,
        }),
      );

      fireFinalPriceEvent(!displayPrice.value.isFromPrice, undefined);
      firePriceChangedEvent();
    }

    function getActiveArticleIdChainWithoutEntsDummyValues(includeInitialSalesInformationId, articleId = undefined) {
      const jsonArticle = articleId ? articles[articleId] : state.active;
      const productId = state.productId;
      if (jsonArticle) {
        const idChain = {
          syan: productId,
          // articleId: optional,
          // colorId: optional
          // salesInformationId: optional
        };
        if (jsonArticle.colors) {
          idChain.colorId = jsonArticle.colors.active.colorId;
        }

        if ((includeInitialSalesInformationId || !jsonArticle.initial) && jsonArticle.salesInformationId) {
          idChain.salesInformationId = jsonArticle.salesInformationId;
        }

        if (!jsonArticle.initial) {
          idChain.articleId = jsonArticle.salesId;
        }
        return idChain;
      }
      return null;
    }

    function sendPageviewTracking() {
      trackCubePageViewPds();
    }

    async function fetchWishlistItems() {
      try {
        if (window.location.href.includes('.ents.breuni.de')) {
          // Skip when request doesn't come from the frontend proxy
          return;
        }

        let requestItems;
        if (state.active.colors && state.active.colors.values.length > 0) {
          requestItems = state.active.colors.values.map((color) => ({
            syan,
            colorId: color.colorId,
          }));
        } else {
          requestItems = [
            {
              syan,
              colorId: null,
            },
          ];
        }

        const response = await fetch(`${mandatedHost}ents/api/v1/wishlist/contains`, {
          signal: AbortSignal.timeout(wishlistApiTimeout),
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestItems),
          credentials: 'include',
        });

        const responseData = await response.json();
        wishlistItems.value = responseData.map((i) => `${i.syan.toString()}${i.colorId ? '-' : ''}${i.colorId ?? ''}`);
      } catch {
        // timeout or invalid data / response json
      }
    }

    const syanColorsOnWishlist = computed(() => {
      return JSON.stringify(wishlistItems.value);
    });

    EventBus.on('ents:vue:flags:update', (flags) => {
      isEasyIron.value = flags.isEasyIron;
      isNew.value = flags.isNew;
    });

    EventBus.on('ents:out-of-stock-layer:show', (salesInformationId) => {
      document.dispatchEvent(
        new CustomEvent('pruefen:best:wvb-overlay:open', {
          detail: {
            vertriebsinfoId: salesInformationId,
            source: 'pds',
          },
        }),
      );
    });

    onMounted(() => {
      EventBus.on('ents:vue:color:changed', colorChange);
      EventBus.on('ents:vue:color:preview', colorPreview);
      EventBus.on('ents:vue:article:changed', articleChange);
      EventBus.on('ents:vue:dimension:select', dimensionChange);
      EventBus.on('ents:vue:layer:hide', layerHide);
      EventBus.on('ents:vue:product-family:created', () => {
        updateProductFamilyUri();
      });

      nextTick(() => {
        if (isInitialArticle.value) {
          // select single available article for dimensions with only one article
          selectInitialArticle();
        } else {
          // update variant in URL if needed
          rewriteURI(true);
        }

        if (hasAliasArticle.value) {
          selectAliasArticle();
        }

        sendProductViewTracking();

        const body = document.getElementsByTagName('body')[0];
        body.classList.add('bewerten-body--ready');
        document.dispatchEvent(new CustomEvent('bewe:ents:vue-ready'));
      });

      updateProductFamilyUri();
      changeMetaLanguageHeader();
      window.performance.mark('bewe.pds.vue.mounted');
      window.performance.measure('bewe.pds.vue.ready', 'domInteractive', 'bewe.pds.vue.mounted');
      sendPageviewTracking();
      document.dispatchEvent(new CustomEvent('camp:aktionsflags:initialized'));
      updateCombiModuleColor();
      updateVspColor();
      window.addEventListener('keydown', boundStopColorPreview);

      window.addEventListener('pageshow', onPageShow);
      fetchWishlistItems();
      if (fitaRecoCall) {
        fetchFitaReco();
      }
    });

    async function fetchFitaReco() {
      const colorId = getActiveOrRepresentativeColorId();
      const colorParam = colorId ? `&colorId=${colorId}` : '';
      fetch(
        `${mandatedHost}brtn/api/fitanalytics/recommendation?syan=${state.productId}${colorParam}`,
        { signal: AbortSignal.timeout(1000) },
        { method: 'GET' },
        { credentials: 'include' },
      )
        .then((res) => (res.status === 200 ? res.json() : console.info('Nothing valid from fita reco !')))
        .then((json) => {
          if (json.articleId in state.articleMap) {
            articleChange(json.articleId);
            trackSizeSelected(
              state.productId,
              json.articleId,
              state.active.salesInformationId,
              state.active.colors?.active?.colorId,
              state.active.outofstock,
              'size_recommendation',
            );
          }
        })
        .catch((error) => {
          if (error.name === 'TimeoutError') {
            console.info('FitAnalytics recommendation request timed out');
          }
        });
    }

    const boundStopColorPreview = stopColorPreview.bind(this);

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', 'keydown', boundStopColorPreview);
      window.removeEventListener('pageshow', onPageShow);
    });

    function onPageShow(e) {
      if (e.persisted) {
        fetchWishlistItems();
      }
    }

    function stopColorPreview(event) {
      if (event.key === 'Escape') {
        EventBus.emit('ents:vue:color:preview', null);
      }
    }

    trackABTests();

    return {
      ...toRefs(state),
      articles,
      productDetails,
      displayPrice,
      getActiveArticleIdChainWithoutEntsDummyValues,
      mandatoryDimension,
      isInitialArticle,
      isNew,
      getArticleForColor,
      syanColorsOnWishlist,
    };
  },
});
